import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DataGrid, { Column, DataGridTypes, GroupPanel, Lookup, Paging, Selection } from 'devextreme-react/data-grid';
import Form, { GroupItem, Label, SimpleItem } from "devextreme-react/form";
import './gestion.activites.css';
import { Button } from 'devextreme-react';
import RadioGroup from 'devextreme-react/radio-group';
import { FieldDataChangedEvent } from 'devextreme/ui/form';
import IActivite from "../../models/IActivite";
import IGestionActivite from "../../models/IGestionActivite";
import { Mode } from "../../hooks/useCustomDataSource";
import useLookUpBase from '../../components/selectbox/LookupBase';
import Guid from "../../models/Guid";
import ILookupItem from '../../models/ILookupItem';
import IActivitePointEau from '../../models/IActivitePointEau';

const estLookupValide = (item: ILookupItem) =>
  !item.id.isEmpty() && item.description?.trim().toLowerCase() !== "aucun";

const pointsEauDisponibilite = [
  { text: "Tous les points d'eau sont disponibles pour l'activité" },
  { text: "Limiter les points d'eau pour l'activité" }
];

interface LookupSelection {
  id: string;
  description?: string;
}

export interface IGestionActivitePointsEauProps {
  mode: Mode;
  selectedActivite: IGestionActivite;
  setSelectedDataRow: React.Dispatch<React.SetStateAction<IGestionActivite>>;
  setIsFieldModifier: (value: boolean) => void;
  onFieldDataChanged: (e: FieldDataChangedEvent) => void;
}

export default function GestionActivitePointsEau({
  mode,
  selectedActivite,
  setSelectedDataRow,
  setIsFieldModifier,
  onFieldDataChanged,
}: IGestionActivitePointsEauProps) {
  const { editorOptions: pointEauEditorOptions, data: tousLesPointsEau } = useLookUpBase({
    isModeModifier: mode !== Mode.Lecture,
    controllerName: "pointEau",
    showClear: mode !== Mode.Lecture,
    setDataRow: () => { },
    includeEmpty: false
  });

  const addPointEauTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const skipRadioDirtyRef = useRef(false);

  const [radioPointsEau, setRadioPointsEau] = useState<string>(() =>
    selectedActivite?.activitePointEaux?.some((item) => !item.isDeleted && !item.idPointEau.isEmpty())
      ? pointsEauDisponibilite[1].text
      : pointsEauDisponibilite[0].text
  );
  const [selectedPointEau, setSelectedPointEau] = useState<(IActivitePointEau & { description?: string })>();
  const [selectedDispoPointEau, setSelectedDispoPointEau] = useState<LookupSelection>();

  const activitePointEaux = useMemo(
    () => selectedActivite?.activitePointEaux ?? [],
    [selectedActivite?.activitePointEaux]
  );

  const cataloguePointsEau = useMemo(
    () => (tousLesPointsEau ?? []).filter(estLookupValide),
    [tousLesPointsEau]
  );

  const pointEauEditorOptionsFiltres = useMemo(
    () => ({ ...pointEauEditorOptions, dataSource: cataloguePointsEau }),
    [pointEauEditorOptions, cataloguePointsEau]
  );

  const activitePointEauxAffichage = useMemo(
    () => activitePointEaux
      .filter((item) => !item.isDeleted && !item.idPointEau.isEmpty())
      .map((item) => ({
        ...item,
        description: cataloguePointsEau.find((p) => p.id.equals(item.idPointEau))?.description ?? ""
      })),
    [activitePointEaux, cataloguePointsEau]
  );

  const pointsEauDisponiblesAjout = useMemo(
    () => cataloguePointsEau.filter(
      (pointEau) => !activitePointEaux.some((item) => item.idPointEau.equals(pointEau.id) && !item.isDeleted)
    ),
    [cataloguePointsEau, activitePointEaux]
  );

  const pointsEauEnEdition = mode !== Mode.Lecture && radioPointsEau === pointsEauDisponibilite[1].text;

  const onSelectionChangedPointEau = useCallback(({ selectedRowsData }: DataGridTypes.SelectionChangedEvent) => {
    if (mode === Mode.Lecture) return
    setSelectedPointEau(selectedRowsData[0]);
  }, [mode]);

  const onSelectionChangedDispoPointEau = useCallback(({ selectedRowsData }: DataGridTypes.SelectionChangedEvent) => {
    if (mode === Mode.Lecture) return
    setSelectedDispoPointEau(selectedRowsData[0]);
  }, [mode]);

  const addPointEau = useCallback(() => {
    if (!selectedDispoPointEau || !selectedActivite?.idActivite) return

    if (addPointEauTimeoutRef.current) {
      clearTimeout(addPointEauTimeoutRef.current);
    }

    addPointEauTimeoutRef.current = setTimeout(() => {
      if (!selectedDispoPointEau?.id || new Guid(selectedDispoPointEau.id).isEmpty()
        || selectedDispoPointEau.description?.trim().toLowerCase() === "aucun")
        return

      skipRadioDirtyRef.current = true;
      setRadioPointsEau(pointsEauDisponibilite[1].text);
      skipRadioDirtyRef.current = false;
      setSelectedDataRow((prev: IGestionActivite) => {
        const pointEaux = prev.activitePointEaux ?? [];
        const exists = pointEaux.some((item) =>
          item.idPointEau.toString() === selectedDispoPointEau.id && !item.isDeleted
        );

        if (exists) return prev;

        const existingIndex = pointEaux.findIndex((item) =>
          item.idPointEau.toString() === selectedDispoPointEau.id
        );

        if (existingIndex !== -1) {
          const updated = [...pointEaux];
          updated[existingIndex] = { ...updated[existingIndex], isDeleted: false };
          return { ...prev, activitePointEaux: updated };
        }

        return {
          ...prev,
          activitePointEaux: [...pointEaux, {
            idActivite: selectedActivite.idActivite,
            idPointEau: new Guid(selectedDispoPointEau.id),
            isDeleted: false
          } as IActivitePointEau]
        };
      });
      setIsFieldModifier(true);
    }, 300);
  }, [selectedDispoPointEau, selectedActivite?.idActivite, setSelectedDataRow, setIsFieldModifier]);

  const addAllPointEau = useCallback(() => {
    if (!cataloguePointsEau.length || !selectedActivite?.idActivite) return

    skipRadioDirtyRef.current = true;
    setRadioPointsEau(pointsEauDisponibilite[1].text);
    skipRadioDirtyRef.current = false;
    setSelectedDataRow((prev: IGestionActivite) => {
      const updatedPointEaux = [...(prev.activitePointEaux ?? [])];

      cataloguePointsEau.forEach(pointEau => {
        const existingIndex = updatedPointEaux.findIndex((item) =>
          item.idPointEau.equals(pointEau.id)
        );

        if (existingIndex !== -1) {
          updatedPointEaux[existingIndex] = {
            ...updatedPointEaux[existingIndex],
            isDeleted: false
          };
        } else {
          updatedPointEaux.push({
            idActivite: selectedActivite.idActivite,
            idPointEau: pointEau.id,
            isDeleted: false
          } as IActivitePointEau);
        }
      });

      return { ...prev, activitePointEaux: updatedPointEaux };
    });
    setIsFieldModifier(true);
  }, [cataloguePointsEau, selectedActivite?.idActivite, setSelectedDataRow, setIsFieldModifier]);

  const removePointEau = useCallback(() => {
    if (!selectedPointEau?.idPointEau) return

    setSelectedDataRow((prev: IGestionActivite) => {
      const pointEaux = prev.activitePointEaux ?? [];
      const index = pointEaux.findIndex((item) =>
        item.idPointEau.equals(selectedPointEau.idPointEau)
      );

      if (index === -1) return prev;

      const updated = [...pointEaux];
      updated[index] = { ...updated[index], isDeleted: true };
      return { ...prev, activitePointEaux: updated };
    });
    setIsFieldModifier(true);

  }, [selectedPointEau, setSelectedDataRow, setIsFieldModifier]);

  const removeAllPointEau = useCallback(() => {
    if (!activitePointEaux.length) return

    setSelectedDataRow((prev: IGestionActivite) => ({
      ...prev,
      activitePointEaux: (prev.activitePointEaux ?? []).map((item) => ({
        ...item,
        isDeleted: true
      }))
    }));
    setIsFieldModifier(true);
  }, [activitePointEaux.length, setSelectedDataRow, setIsFieldModifier]);

  const onValueChangedPointsEau = useCallback((e: { value?: string; event?: unknown }) => {
    if (e.value == null) return;

    setRadioPointsEau(e.value);

    if (e.value === pointsEauDisponibilite[0].text) {
      setSelectedPointEau(undefined);
      setSelectedDispoPointEau(undefined);
      setSelectedDataRow((prev: IGestionActivite) => ({
        ...prev,
        activitePointEaux: (prev.activitePointEaux ?? []).map((item) => ({
          ...item,
          isDeleted: true
        }))
      }));
    }

    if (!skipRadioDirtyRef.current && e.event != null)
      setIsFieldModifier(true);
  }, [setSelectedDataRow, setIsFieldModifier]);

  useEffect(() => {
    const hasActivePointsEau = activitePointEaux.some((item) => !item.isDeleted && !item.idPointEau.isEmpty());
    skipRadioDirtyRef.current = true;
    setRadioPointsEau(hasActivePointsEau ? pointsEauDisponibilite[1].text : pointsEauDisponibilite[0].text);
    skipRadioDirtyRef.current = false;
  }, [selectedActivite?.idActivite]);

  return (
    <Form readOnly={mode === Mode.Lecture} alignItemLabelsInAllGroups={true} formData={selectedActivite || {} as IActivite} className={"forms"} labelLocation={"left"} showColonAfterLabel={true} onFieldDataChanged={onFieldDataChanged}>
      <GroupItem>
        <GroupItem>
          <RadioGroup
            disabled={mode === Mode.Lecture}
            dataSource={pointsEauDisponibilite}
            value={radioPointsEau}
            onValueChanged={onValueChangedPointsEau}
            displayExpr="text"
            valueExpr="text"
          />
        </GroupItem>
        <GroupItem caption={"Poins d'eau disponible pour cet activité"}>
        </GroupItem>
      </GroupItem>
      <GroupItem colCount={3} cssClass="activite-liens-dual-list">
        <GroupItem>
          <DataGrid
            disabled={!pointsEauEnEdition}
            showBorders={true}
            dataSource={activitePointEauxAffichage}
            allowColumnReordering={false}
            keyExpr="idPointEau"
            onSelectionChanged={onSelectionChangedPointEau}
          >
            <Selection mode="single" />
            <GroupPanel visible={mode === Mode.Lecture} />
            <Column dataField="description" alignment="left" caption="Description" />
            <Column dataField="isDeleted" dataType="boolean" visible={false} />
          </DataGrid>
        </GroupItem>
        <SimpleItem
          cssClass="activite-liens-transfer-item"
          render={() => (
            <div className="activite-liens-transfer">
              <div className="activite-liens-transfer__row">
                <Button text={"<"} disabled={!pointsEauEnEdition || !selectedDispoPointEau} onClick={addPointEau} />
                <Button text={">"} disabled={!pointsEauEnEdition || !selectedPointEau?.idPointEau} onClick={removePointEau} />
              </div>
              <div className="activite-liens-transfer__row">
                <Button text={"<<"} disabled={!pointsEauEnEdition} onClick={addAllPointEau} />
                <Button text={">>"} disabled={!pointsEauEnEdition} onClick={removeAllPointEau} />
              </div>
            </div>
          )}
        >
          <Label visible={false} />
        </SimpleItem>
        <GroupItem>
          <DataGrid
            disabled={!pointsEauEnEdition}
            showBorders={true}
            dataSource={pointsEauDisponiblesAjout}
            allowColumnReordering={false}
            keyExpr="id"
            onSelectionChanged={onSelectionChangedDispoPointEau}
          >
            <Selection mode="single" />
            <GroupPanel visible={mode === Mode.Lecture} />
            <Paging defaultPageSize={10} />
            <Column dataField="id" alignment="left">
              <Lookup {...pointEauEditorOptionsFiltres} />
            </Column>
          </DataGrid>
        </GroupItem>
      </GroupItem>
    </Form>
  );
}
